import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	getMomentTz,
	useUserContext,
} from "@dexteel/mesf-core";
import { Divider, Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { DelaysSankeyGraphic } from "./components/DelaysSankeyGraphic";
import { Filters } from "./components/filters/Filters";
import { SankeyData } from "./models/DelayForSankey";
import { getDelaysForSankey } from "./repositories/DelaysSankeyReportRepository";

const moment = getMomentTz();

type useDelaysSankeyProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeMicroDelays: boolean;
	includeUndeclared: boolean;
	includeRollChange: boolean;
	thresholdPct: number;
	topN: number;
	onError: (error: Error) => void;
};

export const useDelaysSankey = ({
	startDate,
	endDate,
	delayAreaAssetId,
	includeMicroDelays,
	includeUndeclared,
	includeRollChange,
	thresholdPct,
	topN,
	onError,
}: useDelaysSankeyProps) => {
	return useQuery<SankeyData, Error>({
		queryKey: [
			"delaysSankey",
			startDate.getTime(),
			endDate.getTime(),
			delayAreaAssetId,
			includeMicroDelays,
			includeUndeclared,
			includeRollChange,
			thresholdPct,
			topN,
		],
		queryFn: ({ signal }) =>
			getDelaysForSankey(
				{
					startDate,
					endDate,
					delayAreaAssetId,
					includeMicroDelays,
					includeUndeclared,
					includeRollChange,
					thresholdPct,
					topN,
				},
				signal,
			),
		onError: onError,
		enabled: !!startDate && !!endDate && !!delayAreaAssetId,
	});
};

export const DelaysSankeyReportPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [includeMicrodelays, setIncludeMicrodelays] = useState<boolean>(true);
	const [includeUndeclaredDelays, setIncludeUndeclaredDelays] =
		useState<boolean>(true);
	const [includeRollChange, setIncludeRollChange] = useState<boolean>(true);
	const [thresholdPct, setThresholdPct] = useState<number>(1);
	const [topN, setTopN] = useState<number>(20);

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		Number(defaultAreaId),
	);

	const { data: sankeyData } = useDelaysSankey({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		delayAreaAssetId: selectedDelayArea,
		includeMicroDelays: includeMicrodelays,
		includeUndeclared: includeUndeclaredDelays,
		includeRollChange,
		thresholdPct,
		topN,
		onError: (e) => setError(e.message),
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
	};

	const handleDelayAreaChange = (assetId: number | null) => {
		setSelectedDelayArea(assetId);
	};

	const emptySankeyData: SankeyData = { nodes: [], links: [] };

	useEffect(() => {
		if (defaultAreaId) {
			setSelectedDelayArea(Number(defaultAreaId) || null);
		}
	}, [defaultAreaId]);

	return (
		<>
			<HelmetDexteel title="Sankey Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Sankey Report
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12 }}>
					<Filters
						onFilterChange={handleFilterChange}
						initialStartDate={filterStartDate}
						initialEndDate={filterEndDate}
						selectedDelayArea={selectedDelayArea}
						onDelayAreaChange={handleDelayAreaChange}
						includeMicrodelays={includeMicrodelays}
						onIncludeMicrodelaysChange={setIncludeMicrodelays}
						includeUndeclaredDelays={includeUndeclaredDelays}
						onIncludeUndeclaredDelaysChange={setIncludeUndeclaredDelays}
						includeRollChange={includeRollChange}
						onIncludeRollChangeChange={setIncludeRollChange}
						thresholdPct={thresholdPct}
						onThresholdPctChange={setThresholdPct}
						topN={topN}
						onTopNChange={setTopN}
					/>
				</Grid2>

				<Grid2 size={{ md: 12 }}>
					<Divider />
				</Grid2>

				<Grid2 size={{ md: 12 }}>
					<DelaysSankeyGraphic sankeyData={sankeyData || emptySankeyData} />
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
